import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

/**
 * Names the browser tab for the current route.
 *
 * The console is a single-page app, so the only <title> that ever existed was the static one in
 * index.html. Every screen showed "Mintly - Control Center", which makes two console tabs
 * indistinguishable and makes browser history useless for finding your way back to a record.
 *
 * Kept as one route-to-title map rather than a call in each of the fifty page components. A page
 * that has to remember to set its own title is a page that will eventually forget, and the map
 * doubles as a readable index of what the console actually contains.
 *
 * Patterns are react-router paths and are matched with `matchPath`, longest first, so
 * `/companies/new` wins over `/companies/:companyId`. Detail routes get the entity name rather
 * than the id: "Employer" is more use in a tab than "6a785544388b6dc222cb7b2c", and the record's
 * own name is not known here. A page that wants the record name can still set `document.title`
 * itself afterwards; this establishes the floor, not a ceiling.
 */

const SUFFIX = 'Mintly Control Center';

/** Ordered most specific first. `matchPath` is exact, so order only settles genuine overlaps. */
export const ROUTE_TITLES: ReadonlyArray<readonly [string, string]> = [
  ['/login', 'Sign in'],

  ['/dashboard', 'Dashboard'],
  ['/dashboard/:tab', 'Dashboard'],
  ['/analytics', 'Analytics'],
  ['/analytics/:tab', 'Analytics'],

  ['/jobs/new', 'New job'],
  ['/jobs/:jobId/:tab', 'Job'],
  ['/jobs/:jobId', 'Job'],
  ['/jobs', 'Jobs'],

  ['/companies/new', 'New company'],
  ['/companies/:companyId/:tab', 'Company'],
  ['/companies/:companyId', 'Company'],
  ['/companies', 'Companies'],
  ['/company-requests/:requestId', 'Company request'],
  // The list, now that it has a route of its own rather than a tab on /companies.
  ['/company-requests', 'Company access requests'],

  ['/jobseekers/import', 'Import jobseekers'],
  ['/jobseekers/:jobSeekerId/:tab', 'Jobseeker'],
  ['/jobseekers/:jobSeekerId', 'Jobseeker'],
  ['/jobseekers', 'Jobseekers'],

  ['/employers/:employerId/:tab', 'Employer'],
  ['/employers/:employerId', 'Employer'],
  ['/employers', 'Employers'],

  ['/job-applications/:applicationId/:tab', 'Application'],
  ['/job-applications/:applicationId', 'Application'],
  ['/job-applications', 'Job applications'],
  ['/applications', 'Applications'],
  ['/applications/:tab', 'Applications'],

  ['/hr', 'HR'],
  ['/my-accounts', 'Premium Hiring Clients'],
  ['/leads/:leadId', 'Lead'],
  ['/leads', 'Leads'],
  ['/pipeline', 'Pipeline'],
  ['/promo-codes/new', 'New promo code'],
  ['/promo-codes/:promoId/:tab', 'Promo code'],
  ['/promo-codes/:promoId', 'Promo code'],
  ['/promo-codes', 'Promo codes'],
  ['/subscriptions', 'Subscriptions'],
  ['/free-job-alerts', 'Free job alerts'],

  // Record routes before the :tab pattern — matchPath is exact, but order settles the overlap
  // between `/email-campaigns/emails/:key` and anything that could match two segments.
  ['/email-campaigns/emails/:templateKey/:tab', 'Email'],
  ['/email-campaigns/emails/:templateKey', 'Email'],
  ['/email-campaigns/drip-sequences/:sequenceId', 'Drip sequence'],
  // Still declared, as a permanent alias. Titles are kept for renamed paths too, or the route
  // exists with no title and the tab falls back to the bare suffix.
  ['/email-campaigns/drips/:sequenceId', 'Drip sequence'],
  ['/email-campaigns', 'Email operations'],
  ['/email-campaigns/:tab', 'Email operations'],
  ['/email-templates', 'Email templates'],
  ['/email-analytics', 'Email analytics'],
  ['/email-tracking', 'Email tracking'],
  ['/automated-emails', 'Automated emails'],
  ['/drip-campaigns', 'Drip campaigns'],

  ['/guides/new', 'New guide'],
  ['/guides/import/batches/:batchId', 'Import batch'],
  ['/guides/import/batches', 'Import batches'],
  ['/guides/import', 'Bulk import'],
  ['/guides/:guideId', 'Guide'],
  ['/guides', 'Guides'],
  ['/seo/pages/:pageId/:tab', 'SEO page'],
  ['/seo/pages/:pageId', 'SEO page'],
  ['/seo', 'SEO, GEO, AEO'],

  ['/agreements', 'Agreements'],
  /* The three old Contracts paths only redirect to /agreements now, but they are still declared
     routes and the title map has to cover every one of them. The title is what the tab says for
     the instant before the redirect lands. */
  ['/contracts', 'Agreements'],
  ['/contracts/new', 'Agreements'],
  ['/contracts/:contractId', 'Agreements'],

  ['/notifications', 'Notifications'],
  ['/referrals/people/:jobSeekerId', 'Referral'],
  ['/referrals', 'Referrals'],
  ['/ambassadors/:ambassadorId/:tab', 'HR Ambassador'],
  ['/ambassadors/:ambassadorId', 'HR Ambassador'],
  ['/ambassadors', 'HR Ambassadors'],
  ['/external-jobs/:sourceId/:tab', 'External jobs source'],
  ['/external-jobs/:sourceId', 'External jobs source'],
  ['/external-jobs', 'External jobs'],
  ['/notifications/:tab', 'Notifications'],

  ['/user-rbac/new', 'New user'],
  ['/user-rbac/:userId/:tab', 'User'],
  ['/user-rbac/:userId', 'User'],
  ['/user-rbac', 'User RBAC'],
  ['/user-details', 'User details'],
  ['/job-metadata', 'Settings'],
  ['/cloud-costs', 'Cloud costs'],
  ['/cloud-costs/:tab', 'Cloud costs'],
];

/**
 * The title for a pathname, suffix included.
 *
 * Exported for the unit test: the map is only useful if it stays in step with the router, and a
 * test can compare the two lists in a way a reader scanning both files cannot.
 */
export const titleForPath = (pathname: string): string => {
  const hit = ROUTE_TITLES.find(([pattern]) =>
    matchPath({ path: pattern, end: true }, pathname),
  );
  // No match means a route was added without a title. The suffix alone is still better than the
  // previous behaviour, which was the suffix alone on every single page.
  return hit ? `${hit[1]} | ${SUFFIX}` : SUFFIX;
};

/** Mount once, inside the Router. Sets the tab title on every navigation. */
export const useDocumentTitle = (): void => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleForPath(pathname);
  }, [pathname]);
};
